import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as authApi from '../api/auth';

export const SignUp: React.FC = () => {
  const [signupRole, setSignupRole] = useState<'family' | 'managing_carer'>('family');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientDob, setPatientDob] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.signup({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        role: signupRole,
        ...(signupRole === 'managing_carer' && {
          patient_first_name: patientFirstName,
          patient_last_name: patientLastName,
          patient_date_of_birth: patientDob,
        }),
      });
      navigate('/verify-email', { state: { email } });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-bold text-center">Create Account</h2>
        {error && <p className="text-red-600 text-sm text-center">{error}</p>}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setSignupRole('family')}
            className={`flex-1 py-3 rounded border text-center ${signupRole === 'family' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border-gray-300'}`}
          >
            Family
          </button>
          <button
            type="button"
            onClick={() => setSignupRole('managing_carer')}
            className={`flex-1 py-3 rounded border text-center ${signupRole === 'managing_carer' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border-gray-300'}`}
          >
            Managing Carer
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password (min 8 chars)</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label>
              <input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
              <input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
            </div>
          </div>
          {signupRole === 'managing_carer' && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-medium text-gray-900">Patient Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="patientFirstName" className="block text-sm font-medium text-gray-700">Patient First Name</label>
                  <input id="patientFirstName" value={patientFirstName} onChange={(e) => setPatientFirstName(e.target.value)} required className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label htmlFor="patientLastName" className="block text-sm font-medium text-gray-700">Patient Last Name</label>
                  <input id="patientLastName" value={patientLastName} onChange={(e) => setPatientLastName(e.target.value)} required className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
                </div>
              </div>
              <div>
                <label htmlFor="patientDob" className="block text-sm font-medium text-gray-700">Date of Birth</label>
                <input id="patientDob" type="date" value={patientDob} onChange={(e) => setPatientDob(e.target.value)} required className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" />
              </div>
            </div>
          )}
          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>
        <p className="text-sm text-gray-600 text-center">
          Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
};